export const MERCHANT_REVENUE_PROJECTION_MODES = [
  {
    value: "flat_nominal",
    label: "Flat nominal",
    fullLabel: "Flat nominal merchant revenue",
    description:
      "Uses the simulated Year 1 merchant revenue as the same nominal value for every project year.",
  },
  {
    value: "inflation_indexed",
    label: "Indexed to inflation",
    fullLabel: "Indexed to inflation",
    description:
      "Escalates Year 1 merchant revenue by the project inflation rate each year.",
  },
  {
    value: "custom_escalation",
    label: "Custom annual escalation",
    fullLabel: "Custom annual escalation",
    description:
      "Escalates Year 1 merchant revenue by a user-defined annual percentage.",
  },
  {
    value: "custom_curve_multipliers",
    label: "Custom curve - multipliers",
    fullLabel: "Custom annual curve - multipliers",
    description:
      "Applies user-defined annual multipliers to Year 1 merchant revenue.",
  },
  {
    value: "custom_curve_revenues",
    label: "Custom curve - revenues",
    fullLabel: "Custom annual revenue curve",
    description:
      "Uses user-defined merchant revenue values for each project year.",
  },
  {
    value: "forward_curve_redispatch",
    label: "Forward price curve (re-dispatch)",
    fullLabel: "Forward market price curve with per-year re-dispatch",
    description:
      "Scales the hourly market price curve to a user-supplied annual EUR/MWh level path " +
      "(e.g. an external/advisor forward curve) and re-runs the dispatch optimization for " +
      "each forward year, so spread capture is modelled instead of escalated. The level " +
      "path is an input, not an in-house long-term price forecast.",
  },
];

export const FORWARD_LEVEL_INPUT_MODES = [
  {
    value: "levels_eur_mwh",
    label: "Absolute levels (€/MWh)",
    // Inside the sentence under the table; its own text node, so it translates.
    inlineLabel: "absolute levels (€/MWh)",
    columnLabel: "Price Level (€/MWh)",
    description:
      "One average DAM price per project year, in €/MWh. Blank years carry the previous value.",
  },
  {
    value: "multipliers_vs_base",
    label: "Multipliers vs simulation year",
    inlineLabel: "multipliers of the simulation-year price",
    columnLabel: "Multiplier (×)",
    description:
      "Per-year multiplier applied to the simulation-year average price (1.0 = same level).",
  },
  {
    value: "deltas_chained_pct",
    label: "% change vs previous year (chained)",
    inlineLabel: "% changes vs the previous year (chained)",
    columnLabel: "Δ vs previous year (%)",
    description:
      "Year-on-year percentage change, compounded: year 2 = -2 means 2% below year 1. Blank = 0%.",
  },
  {
    value: "deltas_vs_base_pct",
    label: "% change vs simulation year",
    inlineLabel: "% changes vs the simulation year",
    columnLabel: "Δ vs simulation year (%)",
    description:
      "Percentage offset of each year vs the simulation-year average: -10 means 10% below it.",
  },
];

export const FORWARD_CURVE_SCENARIOS = [
  { value: "central", label: "Central" },
  { value: "low", label: "Low (conservative)" },
  { value: "high", label: "High" },
];

export const FORWARD_REDISPATCH_GRANULARITIES = [
  {
    value: "representative",
    label: "Representative years (1, 5, 10, 15, 20…) + interpolation",
  },
  { value: "all", label: "Every project year (slower)" },
];

export const DEFAULT_FORWARD_CURVE = {
  levelInputMode: "levels_eur_mwh",
  levelsEurMwh: [],
  levelMultipliers: [],
  levelDeltasPct: [],
  scenario: "central",
  lowDeltaPct: -15,
  highDeltaPct: 15,
  merchantHaircutPct: 0,
  redispatchGranularity: "representative",
  levelSource: "",
  // D1: intraday-spread trajectory relative to the price level. 0 = spreads
  // move with the level (the historical behaviour). A positive % narrows the
  // spread each year (chained) BEFORE the forward-year re-dispatch, so the
  // optimizer re-decides dispatch on the compressed-spread curve.
  spreadCompressionPctYear: 0,
  spreadMultipliers: [],
};

// Mirrors REPRESENTATIVE_YEAR_ANCHORS in
// BESS_feasibility/Configuration/forward_price_curves.py. Kept here so the
// form can show the user the exact set of years the engine will solve
// BEFORE a run rather than after it.
export const REPRESENTATIVE_FORWARD_YEAR_ANCHORS = [1, 5, 10, 15, 20, 25, 30, 35, 40];

/** Project years the forward PRICE loop re-dispatches (1-based). */
export function forwardRedispatchYears(projectYears, granularity) {
  const years = Math.max(0, Math.floor(Number(projectYears) || 0));
  if (!years) return [];
  if (String(granularity || "").toLowerCase() === "all") {
    return Array.from({ length: years }, (_, i) => i + 1);
  }
  const set = new Set(REPRESENTATIVE_FORWARD_YEAR_ANCHORS.filter((y) => y <= years));
  set.add(1);
  set.add(years);
  return [...set].sort((a, b) => a - b);
}

/**
 * Years the UNIFIED pass solves: the union of the price anchors and the
 * battery capacity anchors, always including year 1. The two lists do NOT
 * have to agree - a year that appears in only one of them still needs its
 * own pass, and a year in both is solved once instead of twice.
 */
export function unifiedRedispatchYears(projectYears, granularity, capacityYears) {
  const years = Math.max(0, Math.floor(Number(projectYears) || 0));
  if (!years) return [];
  const set = new Set(forwardRedispatchYears(years, granularity));
  (Array.isArray(capacityYears) ? capacityYears : []).forEach((raw) => {
    const y = Math.round(Number(raw));
    if (Number.isFinite(y) && y > 1 && y <= years) set.add(y);
  });
  set.add(1);
  return [...set].sort((a, b) => a - b);
}

// Provenance of the dispatch-realism factor. Mirrors
// DISPATCH_FORESIGHT_PROVENANCE_DEFAULTS in
// BESS_feasibility/Configuration/merchant_revenue_projection.py, which is the
// authority: it decides whether the word "measured" is earned, and it can only
// ever withdraw the claim, never change the applied factor.
//
// FROZEN ON ACCEPT. The measurement is copied into the project once, with the
// window and the date, and does not follow later re-runs of the backtest.
export const DEFAULT_DISPATCH_FORESIGHT_PROVENANCE = {
  basis: "assumption",
  market: "",
  measuredRatio: null,
  daysEvaluated: 0,
  periodStart: "",
  periodEnd: "",
  measuredOn: "",
  socMode: "",
  forecastSource: "",
  // WHOSE BATTERY. "project" = measured on this project's own battery, the
  // basis a lender should be given. "reference" = a market-wide measurement on
  // a fixed reference battery; instant, and disclosed in the report as not
  // being this project's. Empty reads as reference - the weaker claim.
  scope: "",
  bessDescription: "",
  resolutionEraStart: "",
  note: "",
};

// Mirrors DISPATCH_FORESIGHT_MIN_QUOTABLE_DAYS / _MIN_ANNUAL_DAYS /
// _RATIO_TOLERANCE on the backend.
export const DISPATCH_FORESIGHT_MIN_QUOTABLE_DAYS = 90;
export const DISPATCH_FORESIGHT_MIN_ANNUAL_DAYS = 300;
export const DISPATCH_FORESIGHT_RATIO_TOLERANCE = 5e-4;

function isoDateOrEmpty(value) {
  const text = String(value ?? "").trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

export function normalizeDispatchForesightProvenance(raw = {}) {
  const src = raw && typeof raw === "object" ? raw : {};
  const ratio = parseFiniteNumber(src.measuredRatio ?? src.measured_ratio, NaN);
  const days = parseFiniteNumber(src.daysEvaluated ?? src.days_evaluated, 0);
  return {
    basis:
      String(src.basis ?? "").trim().toLowerCase() === "measured"
        ? "measured"
        : "assumption",
    market: String(src.market ?? "").trim().toUpperCase(),
    // A ratio at or above 1 is not a measurement - the forecast dispatch is
    // feasible for the hindsight optimiser, so it cannot beat it - and is
    // dropped rather than shown.
    measuredRatio:
      Number.isFinite(ratio) && ratio > 0 && ratio <= 1 ? ratio : null,
    daysEvaluated: Number.isFinite(days) && days > 0 ? Math.floor(days) : 0,
    periodStart: isoDateOrEmpty(src.periodStart ?? src.period_start),
    periodEnd: isoDateOrEmpty(src.periodEnd ?? src.period_end),
    measuredOn: isoDateOrEmpty(src.measuredOn ?? src.measured_on),
    socMode: String(src.socMode ?? src.soc_mode ?? "").trim().toLowerCase(),
    forecastSource: String(src.forecastSource ?? src.forecast_source ?? "").trim(),
    // Dropped here once, which silently turned every project measurement into
    // a reference one on the way to persistence. The backend is the authority
    // on whether the value is valid; this must at least carry it.
    scope: ["project", "reference"].includes(
      String(src.scope ?? "").trim().toLowerCase()
    )
      ? String(src.scope).trim().toLowerCase()
      : "",
    bessDescription: String(src.bessDescription ?? src.bess_description ?? "").trim(),
    resolutionEraStart: isoDateOrEmpty(
      src.resolutionEraStart ?? src.resolution_era_start
    ),
    note: String(src.note ?? src.notes ?? "").trim(),
  };
}

/**
 * Turn whatever the user pasted into {factor, provenance}.
 *
 * Three shapes are accepted, because all three are things a user actually has
 * in front of them:
 *   1. the block printed by `scripts/capture_backtest.py --emit-config`;
 *   2. the whole backtest artifact, capture_backtest_<MARKET>.json;
 *   3. a bare provenance object.
 * The factor is always taken from the SAME number as measuredRatio, rounded
 * once, so the two can never disagree by construction.
 */
export function parseDispatchForesightMeasurement(text) {
  const raw = String(text ?? "").trim();
  const empty = { ok: false, error: "", warning: "", factor: null, provenance: null, summary: "" };
  if (!raw) return empty;

  let parsed = null;
  try {
    // Pasting the middle of the emitted block gives `"a": 1, "b": {...}`,
    // which is not JSON on its own. Wrapping it is friendlier than rejecting
    // the most likely paste.
    parsed = JSON.parse(raw.startsWith("{") ? raw : `{${raw.replace(/,\s*$/, "")}}`);
  } catch (err) {
    return { ...empty, error: "That is not valid JSON (yet) - paste the whole block." };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ...empty, error: "Expected a JSON object." };
  }

  let provenance = null;
  let ratio = NaN;

  if (parsed.dispatchForesightProvenance || parsed.dispatch_foresight_provenance) {
    provenance = normalizeDispatchForesightProvenance(
      parsed.dispatchForesightProvenance ?? parsed.dispatch_foresight_provenance
    );
    ratio = parseFiniteNumber(
      parsed.dispatchForesightFactor ?? parsed.dispatch_foresight_factor,
      provenance.measuredRatio
    );
  } else if (parsed.suggested_project_config) {
    // A current artifact carries the block the CLI would print. Prefer it: two
    // reconstructions of the same block is how the applied factor and the
    // cited measurement drift apart.
    const inner = parsed.suggested_project_config;
    provenance = normalizeDispatchForesightProvenance(
      inner.dispatchForesightProvenance ?? inner.dispatch_foresight_provenance
    );
    ratio = parseFiniteNumber(
      inner.dispatchForesightFactor ?? inner.dispatch_foresight_factor,
      provenance.measuredRatio
    );
  } else if (parsed.measurement === "dispatch_realism_capture_ratio" || parsed.results) {
    // Fallback for an artifact written before the block existed.
    const mode = String(parsed.headline_soc_mode ?? "carry");
    const slot = (parsed.results && parsed.results[mode]) || {};
    ratio = parseFiniteNumber(slot.aggregate_capture_ratio, NaN);
    provenance = normalizeDispatchForesightProvenance({
      basis: "measured",
      market: parsed.market,
      measuredRatio: ratio,
      daysEvaluated: parsed.days_evaluated,
      periodStart: parsed.period_start,
      periodEnd: parsed.period_end,
      measuredOn: String(parsed.generated_at_utc ?? "").slice(0, 10),
      socMode: mode,
      forecastSource: parsed.forecast_source,
      // Only claim an era when the window contains no forward-filled hourly
      // prices at all. A window that straddles the resolution change is not
      // one measurement, and saying so is the point of the field.
      resolutionEraStart:
        Number(parsed.days_with_hourly_prices_upsampled ?? 1) === 0
          ? parsed.period_start
          : "",
    });
  } else {
    provenance = normalizeDispatchForesightProvenance({ ...parsed, basis: "measured" });
    ratio = parseFiniteNumber(provenance.measuredRatio, NaN);
  }

  if (!Number.isFinite(ratio) || ratio <= 0 || ratio > 1) {
    return {
      ...empty,
      error:
        "No usable capture ratio in that block (it must be above 0 and at most 1).",
    };
  }
  // ONE rounding, applied to both, so the applied factor and the cited
  // measurement are the same number to every decimal either is printed at.
  const rounded = Math.round(ratio * 10000) / 10000;
  provenance = { ...provenance, basis: "measured", measuredRatio: rounded };

  const warnings = [];
  if (provenance.daysEvaluated < DISPATCH_FORESIGHT_MIN_QUOTABLE_DAYS) {
    warnings.push(
      `only ${provenance.daysEvaluated} delivery days - the report will state this as an assumption, not a measurement (${DISPATCH_FORESIGHT_MIN_QUOTABLE_DAYS} needed)`
    );
  } else if (provenance.daysEvaluated < DISPATCH_FORESIGHT_MIN_ANNUAL_DAYS) {
    warnings.push(
      `${provenance.daysEvaluated} delivery days is less than a full year, so the report will say the window is not seasonally complete`
    );
  }
  if (!provenance.market || !provenance.periodStart || !provenance.periodEnd || !provenance.measuredOn) {
    warnings.push("some provenance fields are missing, so the claim will be downgraded to an assumption");
  }

  return {
    ok: true,
    error: "",
    warning: warnings.join("; "),
    factor: rounded,
    provenance,
    summary:
      `${rounded.toFixed(4)} - ${provenance.market || "market not stated"}, ` +
      `${provenance.daysEvaluated} days` +
      (provenance.periodStart && provenance.periodEnd
        ? `, ${provenance.periodStart} to ${provenance.periodEnd}`
        : "") +
      (provenance.measuredOn ? `, measured ${provenance.measuredOn}` : "") +
      (provenance.socMode ? `, SoC mode ${provenance.socMode}` : ""),
  };
}

/**
 * Which dispatch-realism basis a project is on.
 *
 * Extracted from the form because it was NOT testable there, and it was wrong:
 * "Manual assumption" and "No haircut (1.00)" are both assumptions, and the
 * only thing separating them in the stored config is whether the number
 * happens to be 1. Deriving the selection from the factor alone trapped the
 * user - "No haircut" set the factor to 1, and then choosing "Manual" left it
 * at 1, so the select snapped straight back. The user's choice between two
 * bases that store the same thing is its own fact and has to be passed in.
 *
 * A MEASURED basis needs no such help: the project records the scope.
 */
export function dispatchForesightBasisFor({ factor, provenance, manualChosen = false }) {
  const p = normalizeDispatchForesightProvenance(provenance);
  if (p.basis === "measured") {
    return p.scope === "project" ? "project" : "reference";
  }
  if (manualChosen) return "manual";
  // A factor other than 1 can only have been typed, so it is a manual
  // assumption whether or not this session is the one that typed it.
  return parseFiniteNumber(factor, 1) === 1 ? "none" : "manual";
}

/**
 * Mirror of the backend's "is the word measured earned" checks, so the user
 * sees the problem in the form instead of discovering it in the PDF. The
 * backend remains the authority; this only reports.
 */
/**
 * The project's battery as "P MW / E MWh" when the measurement named in
 * `description` ("1 MW / 2.15 MWh, ...") was taken on a materially different
 * one (power or energy more than 5% apart); "" when they agree or either side
 * cannot be read. Same rule as the report.
 */
export function foresightBatteryMismatch(description, powerKw, energyKwh) {
  const found = String(description || "").match(/^\s*([\d.]+)\s*MW\s*\/\s*([\d.]+)\s*MWh/);
  const kw = Number(powerKw);
  const kwh = Number(energyKwh);
  if (!found || !(kw > 0) || !(kwh > 0)) return "";
  const same =
    Math.abs((Number(found[1]) * 1000) / kw - 1) <= 0.05 &&
    Math.abs((Number(found[2]) * 1000) / kwh - 1) <= 0.05;
  if (same) return "";
  const fmt = (value) => String(Number(value.toPrecision(3)));
  return `${fmt(kw / 1000)} MW / ${fmt(kwh / 1000)} MWh`;
}

/**
 * The label for the basis select when the project applies a measurement that
 * is NOT the one the platform holds for that scope - a provenance copied with
 * the project (often from another battery), or an earlier measurement kept
 * frozen. Without it the select names the platform's latest measurement (e.g.
 * "this project's battery, 0.840, not usable") while a different figure is
 * applied. "" when the applied figure is the platform's own.
 */
export function frozenForesightOption({ factor, provenance, serverMeasurement = null, otherBattery = "" }) {
  const p = normalizeDispatchForesightProvenance(provenance);
  if (p.basis !== "measured") return "";
  const applied = parseFiniteNumber(factor, NaN);
  if (!Number.isFinite(applied)) return "";
  const sameAsServer =
    Boolean(serverMeasurement) && Math.abs(Number(serverMeasurement.factor) - applied) <= 5e-4;
  if (sameAsServer && !otherBattery) return "";
  const measuredBattery = p.bessDescription.match(/^\s*([\d.]+\s*MW\s*\/\s*[\d.]+\s*MWh)/);
  const detail =
    otherBattery && measuredBattery
      ? `another battery: ${measuredBattery[1]}`
      : serverMeasurement
        ? "earlier measurement"
        : "";
  return `Applied measurement — ${applied.toFixed(3)}${detail ? ` (${detail})` : ""}`;
}

export function dispatchForesightProvenanceIssue(factor, provenance) {
  const p = normalizeDispatchForesightProvenance(provenance);
  if (p.basis !== "measured") return "";
  const missing = [
    ["market", p.market],
    ["days evaluated", p.daysEvaluated],
    ["period start", p.periodStart],
    ["period end", p.periodEnd],
    ["measurement date", p.measuredOn],
  ]
    .filter(([, value]) => !value)
    .map(([name]) => name);
  if (missing.length) return `Incomplete measurement provenance: ${missing.join(", ")}.`;
  if (p.measuredRatio === null) return "No measured ratio recorded.";
  if (p.periodEnd < p.periodStart) return "The measurement period ends before it starts.";
  if (p.daysEvaluated < DISPATCH_FORESIGHT_MIN_QUOTABLE_DAYS) {
    return `Only ${p.daysEvaluated} days evaluated; ${DISPATCH_FORESIGHT_MIN_QUOTABLE_DAYS} are needed to quote a measured ratio.`;
  }
  const applied = parseFiniteNumber(factor, NaN);
  if (!Number.isFinite(applied)) return "";
  if (applied > p.measuredRatio + DISPATCH_FORESIGHT_RATIO_TOLERANCE) {
    return `The applied factor ${applied} is ABOVE the measured ${p.measuredRatio}; the measurement does not support it, so the report will call it an assumption.`;
  }
  if (applied < p.measuredRatio - DISPATCH_FORESIGHT_RATIO_TOLERANCE) {
    return `Applied ${((p.measuredRatio - applied) * 100).toFixed(1)} points below the measured ${p.measuredRatio} - reported as a deliberate conservative margin.`;
  }
  return "";
}

export const DEFAULT_MERCHANT_REVENUE_PROJECTION = {
  mode: "flat_nominal",
  annualEscalationPct: 0,
  applyInflationEscalation: false,
  applyBatteryDegradation: false,
  // Opt-in. Off = the price loop and the capacity loop each run their own
  // full-year passes and the two results are multiplied (existing
  // behaviour). On = one pass per year carrying both effects at once.
  unifiedRedispatch: false,
  // Dispatch-realism (foresight) factor. The engine's dispatch optimisation
  // sees each day's prices in advance (perfect foresight); realistic
  // day-ahead bidding on forecasts captures less. Values of 0.85-0.95 are
  // typical in published forecast-vs-hindsight BESS arbitrage studies.
  // 1 = no haircut - the default, so existing projects are unchanged.
  // Applied share-aware under Category 11A (battery-at-DAM leg only).
  dispatchForesightFactor: 1,
  // Where that factor came from. Defaults to "assumption", so an existing
  // project reads and renders exactly as before.
  dispatchForesightProvenance: { ...DEFAULT_DISPATCH_FORESIGHT_PROVENANCE },
  customAnnualMultipliers: [],
  customAnnualRevenues: [],
  forwardCurve: { ...DEFAULT_FORWARD_CURVE },
  notes: "",
};

const VALID_MERCHANT_REVENUE_PROJECTION_MODES = new Set(
  MERCHANT_REVENUE_PROJECTION_MODES.map((mode) => mode.value)
);

function normalizeMode(mode) {
  return VALID_MERCHANT_REVENUE_PROJECTION_MODES.has(mode)
    ? mode
    : DEFAULT_MERCHANT_REVENUE_PROJECTION.mode;
}

function parseFiniteNumber(value, fallback) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseNonNegativeCurveValue(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function parseSignedCurveValue(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function prepareSignedCurveValues(values, valueKeys) {
  if (!Array.isArray(values)) return [];
  return trimTrailingMissing(
    values.map((entry) => parseSignedCurveValue(getCurveEntryValue(entry, valueKeys)))
  );
}

function getCurveEntryValue(entry, valueKeys) {
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    const key = valueKeys.find((candidate) => entry[candidate] !== undefined);
    return key ? entry[key] : "";
  }
  return entry;
}

function trimTrailingMissing(values) {
  const next = [...values];
  while (next.length && (next[next.length - 1] === "" || next[next.length - 1] == null)) {
    next.pop();
  }
  return next;
}

function prepareCurveValues(values, valueKeys) {
  if (!Array.isArray(values)) return [];
  return trimTrailingMissing(
    values.map((entry) => parseNonNegativeCurveValue(getCurveEntryValue(entry, valueKeys)))
  );
}

function normalizeForwardCurve(source = {}) {
  const raw =
    source && typeof source === "object" && !Array.isArray(source) ? source : {};
  const levelInputMode = FORWARD_LEVEL_INPUT_MODES.some(
    (m) => m.value === raw.levelInputMode
  )
    ? raw.levelInputMode
    : DEFAULT_FORWARD_CURVE.levelInputMode;
  const scenario = FORWARD_CURVE_SCENARIOS.some((s) => s.value === raw.scenario)
    ? raw.scenario
    : DEFAULT_FORWARD_CURVE.scenario;
  const granularity = FORWARD_REDISPATCH_GRANULARITIES.some(
    (g) => g.value === raw.redispatchGranularity
  )
    ? raw.redispatchGranularity
    : DEFAULT_FORWARD_CURVE.redispatchGranularity;
  return {
    ...DEFAULT_FORWARD_CURVE,
    ...raw,
    levelInputMode,
    scenario,
    redispatchGranularity: granularity,
    levelsEurMwh: Array.isArray(raw.levelsEurMwh)
      ? raw.levelsEurMwh
      : Array.isArray(raw.levels_eur_mwh)
        ? raw.levels_eur_mwh
        : DEFAULT_FORWARD_CURVE.levelsEurMwh,
    levelMultipliers: Array.isArray(raw.levelMultipliers)
      ? raw.levelMultipliers
      : Array.isArray(raw.level_multipliers)
        ? raw.level_multipliers
        : DEFAULT_FORWARD_CURVE.levelMultipliers,
    levelDeltasPct: Array.isArray(raw.levelDeltasPct)
      ? raw.levelDeltasPct
      : Array.isArray(raw.level_deltas_pct)
        ? raw.level_deltas_pct
        : DEFAULT_FORWARD_CURVE.levelDeltasPct,
    spreadCompressionPctYear:
      raw.spreadCompressionPctYear ??
      raw.spread_compression_pct_year ??
      DEFAULT_FORWARD_CURVE.spreadCompressionPctYear,
    spreadMultipliers: Array.isArray(raw.spreadMultipliers)
      ? raw.spreadMultipliers
      : Array.isArray(raw.spread_multipliers)
        ? raw.spread_multipliers
        : DEFAULT_FORWARD_CURVE.spreadMultipliers,
  };
}

function prepareForwardCurveForPersistence(forwardCurve = {}) {
  const normalized = normalizeForwardCurve(forwardCurve);
  return {
    levelInputMode: normalized.levelInputMode,
    levelsEurMwh: prepareCurveValues(normalized.levelsEurMwh, [
      "levelEurMwh",
      "priceEurMwh",
      "value",
    ]),
    levelMultipliers: prepareCurveValues(normalized.levelMultipliers, [
      "multiplier",
      "value",
    ]),
    levelDeltasPct: prepareSignedCurveValues(normalized.levelDeltasPct, [
      "deltaPct",
      "value",
    ]),
    scenario: normalized.scenario,
    lowDeltaPct: parseFiniteNumber(normalized.lowDeltaPct, DEFAULT_FORWARD_CURVE.lowDeltaPct),
    highDeltaPct: parseFiniteNumber(normalized.highDeltaPct, DEFAULT_FORWARD_CURVE.highDeltaPct),
    merchantHaircutPct: Math.min(
      100,
      Math.max(
        0,
        parseFiniteNumber(
          normalized.merchantHaircutPct,
          DEFAULT_FORWARD_CURVE.merchantHaircutPct
        )
      )
    ),
    redispatchGranularity: normalized.redispatchGranularity,
    levelSource: String(normalized.levelSource ?? ""),
    ...(normalized.housePathStartYear != null ? {
      housePathStartYear: Number(normalized.housePathStartYear),
    } : {}),
    spreadCompressionPctYear: Math.min(
      99,
      Math.max(
        0,
        parseFiniteNumber(
          normalized.spreadCompressionPctYear,
          DEFAULT_FORWARD_CURVE.spreadCompressionPctYear
        )
      )
    ),
    spreadMultipliers: Array.isArray(normalized.spreadMultipliers)
      ? normalized.spreadMultipliers
      : DEFAULT_FORWARD_CURVE.spreadMultipliers,
  };
}

export function normalizeMerchantRevenueProjection(config = {}) {
  const source =
    config?.merchantRevenueProjection && typeof config.merchantRevenueProjection === "object"
      ? config.merchantRevenueProjection
      : {};
  const mode = normalizeMode(source.mode);

  return {
    ...DEFAULT_MERCHANT_REVENUE_PROJECTION,
    ...source,
    mode,
    forwardCurve: normalizeForwardCurve(source.forwardCurve ?? source.forward_curve),
    annualEscalationPct:
      source.annualEscalationPct ??
      source.annual_escalation_pct ??
      DEFAULT_MERCHANT_REVENUE_PROJECTION.annualEscalationPct,
    applyInflationEscalation:
      source.applyInflationEscalation ??
      source.apply_inflation_escalation ??
      mode === "inflation_indexed",
    applyBatteryDegradation:
      source.applyBatteryDegradation ??
      source.apply_battery_degradation ??
      DEFAULT_MERCHANT_REVENUE_PROJECTION.applyBatteryDegradation,
    unifiedRedispatch:
      source.unifiedRedispatch ??
      source.unified_redispatch ??
      DEFAULT_MERCHANT_REVENUE_PROJECTION.unifiedRedispatch,
    dispatchForesightFactor:
      source.dispatchForesightFactor ??
      source.dispatch_foresight_factor ??
      DEFAULT_MERCHANT_REVENUE_PROJECTION.dispatchForesightFactor,
    dispatchForesightProvenance: normalizeDispatchForesightProvenance(
      source.dispatchForesightProvenance ?? source.dispatch_foresight_provenance
    ),
    customAnnualMultipliers: Array.isArray(source.customAnnualMultipliers)
      ? source.customAnnualMultipliers
      : Array.isArray(source.custom_annual_multipliers)
        ? source.custom_annual_multipliers
        : DEFAULT_MERCHANT_REVENUE_PROJECTION.customAnnualMultipliers,
    customAnnualRevenues: Array.isArray(source.customAnnualRevenues)
      ? source.customAnnualRevenues
      : Array.isArray(source.custom_annual_revenues)
        ? source.custom_annual_revenues
        : DEFAULT_MERCHANT_REVENUE_PROJECTION.customAnnualRevenues,
    notes:
      source.notes ??
      source.note ??
      DEFAULT_MERCHANT_REVENUE_PROJECTION.notes,
  };
}

export function prepareMerchantRevenueProjectionForPersistence(config = {}) {
  const normalized = normalizeMerchantRevenueProjection(config);
  const mode = normalizeMode(normalized.mode);

  return {
    mode,
    annualEscalationPct: parseFiniteNumber(
      normalized.annualEscalationPct,
      DEFAULT_MERCHANT_REVENUE_PROJECTION.annualEscalationPct
    ),
    applyInflationEscalation: mode === "inflation_indexed",
    applyBatteryDegradation: Boolean(normalized.applyBatteryDegradation),
    // Only meaningful under the re-dispatch mode; persisted as false in
    // every other mode so a saved project can never carry a switch the
    // engine would ignore.
    unifiedRedispatch:
      mode === "forward_curve_redispatch" && Boolean(normalized.unifiedRedispatch),
    dispatchForesightFactor: Math.min(
      1,
      Math.max(
        0.01,
        parseFiniteNumber(
          normalized.dispatchForesightFactor,
          DEFAULT_MERCHANT_REVENUE_PROJECTION.dispatchForesightFactor
        )
      )
    ),
    // Persisted faithfully, NOT clamped: the backend decides whether the
    // "measured" claim stands and warns when it does not. Two authorities
    // silently disagreeing about that is worse than one saying no.
    dispatchForesightProvenance: normalizeDispatchForesightProvenance(
      normalized.dispatchForesightProvenance
    ),
    customAnnualMultipliers: prepareCurveValues(normalized.customAnnualMultipliers, [
      "multiplier",
      "value",
    ]),
    customAnnualRevenues: prepareCurveValues(normalized.customAnnualRevenues, [
      "merchantRevenueEurYear",
      "merchantRevenueEur",
      "revenueEurYear",
      "revenueEur",
      "value",
    ]),
    forwardCurve: prepareForwardCurveForPersistence(normalized.forwardCurve),
    notes: String(normalized.notes ?? ""),
  };
}

export function normalizeProjectMerchantRevenueProjection(config = {}) {
  return {
    ...config,
    merchantRevenueProjection: normalizeMerchantRevenueProjection(config),
  };
}

export function prepareProjectMerchantRevenueProjectionForPersistence(config = {}) {
  return {
    ...config,
    merchantRevenueProjection: prepareMerchantRevenueProjectionForPersistence(config),
  };
}
